import * as styles from './MeetingMessageComponent.module.less';
import { useMemo, useState } from "react";
import { ButtonVariants, TromanButton, Icons } from "@/twapp/react/components/Buttons";
import { registerReactComponent } from "@/twapp/react/registerReactComponent";
import { Message } from "./Message";
import { IMessageEditorForm, MessageEditor } from "./MessageEditor";
import { IAddMeetingMessage, IUpdateMeetingMessage, MeetingMessage } from "@/twapp/objects/MeetingMessage";
import { useDialog } from "@/twapp/react/hooks/useTromanService";
import _ from "lodash";
import { Spinner } from "@/twapp/react/components/Spinner";

export interface IMeetingMessagesProps {
    // undefined implies that data is still loading
    messages?: MeetingMessage[];
    onAdd?: (message: IAddMeetingMessage) => void;
    onUpdate?: (message: IUpdateMeetingMessage) => void;
    onDelete?: (id: string) => void;
}

interface IActiveForm {
    editing?: MeetingMessage;
    form: IMessageEditorForm;
}

const createNewForm = (): IMessageEditorForm => ({
    message: '',
    subject: '',
    notify: false,
    publish: true,
});

const createEditForm = (message: MeetingMessage) => ({
    message: message.Content,
    subject: message.Subject,
    notify: false,
    publish: message.IsPublished,
});

interface INotificationMarker {
    Id: string;
    timestamp?: Date;
}

type IMessageListItem = MeetingMessage | INotificationMarker;

function IsNotificationMarker(x: IMessageListItem): x is INotificationMarker {
    return !(x instanceof MeetingMessage);
}

function createMessageList(messages?: MeetingMessage[]) {
    let ordered = _.orderBy(messages, x => x.CreatedTimestamp, 'desc');
    let lastNotification = _.maxBy(ordered, x => x.NotificationTimestamp);
    
    let result: IMessageListItem[] = ordered;

    if (lastNotification != null) {
        let timestamp = lastNotification.NotificationTimestamp!;
        let lastIndex;
        for (lastIndex=0;lastIndex<ordered.length;++lastIndex) {
            if (ordered[lastIndex].CreatedTimestamp <= timestamp) {
                break;
            }
        }

        if (lastIndex >= 0) {
            result.splice(lastIndex, 0, { Id: 'NOTIFICATION', timestamp: lastNotification?.NotificationTimestamp });
        }
    }

    return result;
}

export const MeetingMessagesComponent = (props: IMeetingMessagesProps) => {

    const dialog = useDialog();
    const [activeForm, setActiveForm] = useState<IActiveForm>();
    const messageList = useMemo(() => createMessageList(props.messages), [props.messages]);

    const beginEdit = (message?: MeetingMessage) => {
        if (!activeForm) {
            setActiveForm({
                editing: message,
                form: message ? createEditForm(message) : createNewForm(),
            });
        }
    };

    const handleSave = (values: IMessageEditorForm) => {
        if (activeForm) {
            if (activeForm.editing) {
                props.onUpdate && props.onUpdate({
                    Id: activeForm.editing.Id,
                    RowVersion: activeForm.editing.RowVersion,
                    Subject: values.subject,
                    Content: values.message,
                    Notify: values.notify,
                    Publish: values.publish,
                });
            } else {
                props.onAdd && props.onAdd({
                    Subject: values.subject,
                    Content: values.message,
                    Notify: values.notify,
                    Publish: values.publish,
                });
            }

            setActiveForm(undefined);
        }
    };

    const cancelEdit = () => {
        setActiveForm(undefined);
    };

    const handleDelete = (message: MeetingMessage) => {
        if (props.onDelete) {
            dialog.confirm(['Radera meddelande', 'Meddelandet tas bort direkt.']).then(confirmed => {
                if (confirmed && props.onDelete) {
                    props.onDelete(message.Id);
                    setActiveForm(undefined);
                }
            });
        }
    };

    return (
        <div>
            <div className={styles.header}>
                <div>
                    <p>
                        Meddelanden visas på Troman Politiker för deltagarna i mötet.
                        <br/>
                        Notis kan skickas till deltagarnas offentliga e-postadress.
                    </p>
                </div>
                <div>
                    <TromanButton onClick={() => beginEdit()} variant={ButtonVariants.Primary} icon={Icons.Add} text="Nytt meddelande" disabled={!!activeForm || !props.messages}/>
                </div>
            </div>
            {
                props.messages ? (
                    <>
                        {
                            activeForm && !activeForm.editing ? (
                                <div className={styles.messageContainer}>
                                    <MessageEditor form={activeForm.form} onSubmit={handleSave} onCancel={cancelEdit}/>
                                </div>
                            ) : undefined
                        }
                        <div>
                            {messageList.map(
                                x => (
                                    <div className={styles.messageContainer} key={x.Id}>
                                        {
                                            IsNotificationMarker(x) ? (
                                                <div className={styles.lastNotification}>
                                                    <div>Notis senast skickad</div>
                                                    <div>{x.timestamp?.toLocaleString('sv-SE')}</div>
                                                </div>
                                            ) : 
                                            activeForm?.editing?.Id === x.Id ? (
                                                <MessageEditor key={x.Id} message={activeForm.editing} form={activeForm.form} onSubmit={handleSave} onCancel={cancelEdit} onDelete={() => handleDelete(x)}/>
                                            ) : (
                                                <Message key={x.Id} message={x} canEdit={!activeForm} onEdit={() => {beginEdit(x)}}/>
                                            )
                                        }
                                    </div>
                                )
                            )}
                        </div>
                    </>
                ) : (
                    <Spinner/>
                )
            }
        </div>
    );
};

registerReactComponent(MeetingMessagesComponent, 'meetingMessagesComponent', {
    messages: '<',
    onAdd: '<',
    onUpdate: '<',
    onDelete: '<',
});
